import { supabaseClient } from './supabase';

export interface TaskNotificationData {
  taskId: string;
  title: string;
  description?: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  assignedToEmail: string;
  assignedToName: string;
  assignedByEmail: string;
  assignedByName: string;
}

export interface DeveloperInfo {
  id: string;
  email: string;
  full_name?: string;
}

export interface LocalGovAdminRequestEmail {
  requesterName: string;
  requesterEmail: string;
  requesterLocation: string;
  requesterLocationType: string;
}

export interface LocalGovAdminRequestReceiptEmail {
  requesterName: string;
  requesterEmail: string;
  requesterLocation: string;
  requesterLocationType: string;
}

/**
 * Send email notification when a task is assigned
 */
export const sendTaskAssignmentNotification = async (taskData: TaskNotificationData): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if we're in development mode (localhost or any local port)
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '0.0.0.0' ||
                         window.location.port === '3000' ||
                         window.location.port === '3001' ||
                         window.location.port === '3002';
    
    if (isDevelopment) {
      // For local development, just log the email content
      console.log('📧 [LOCAL DEV] Task Assignment Email Notification:');
      console.log('To:', taskData.assignedToEmail);
      console.log('Subject:', `New Task Assigned: ${taskData.title}`);
      console.log('Task Details:', {
        title: taskData.title,
        description: taskData.description,
        category: taskData.category,
        priority: taskData.priority,
        assignedTo: taskData.assignedToName,
        assignedBy: taskData.assignedByName
      });
      console.log('📧 [LOCAL DEV] Email would be sent in production');
      return { success: true };
    }

    // For production, use Supabase Edge Function
    const { data, error } = await supabaseClient.functions.invoke('send-task-email', {
      body: {
        taskData: {
          taskId: taskData.taskId,
          title: taskData.title,
          description: taskData.description,
          category: taskData.category,
          priority: taskData.priority,
          assignedToEmail: taskData.assignedToEmail,
          assignedToName: taskData.assignedToName,
          assignedByEmail: taskData.assignedByEmail,
          assignedByName: taskData.assignedByName
        }
      }
    });

    if (error) {
      console.error('Error calling send-task-email function:', error);
      return { 
        success: false, 
        error: error.message 
      };
    }

    console.log('✅ Task assignment email sent successfully:', data);
    return { success: true };
  } catch (error) {
    console.error('Error sending task assignment notification:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Get developer information by ID
 */
export const getDeveloperInfo = async (developerId: string): Promise<DeveloperInfo | null> => {
  try {
    const { data, error } = await supabaseClient
      .from('user_profiles')
      .select('id, email, full_name')
      .eq('id', developerId)
      .single();

    if (error) {
      console.error('Error fetching developer info:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching developer info:', error);
    return null;
  }
};

/**
 * Get current user information
 */
export const getCurrentUserInfo = async (): Promise<DeveloperInfo | null> => {
  try {
    const { data: { user } } = await supabaseClient.auth.getUser();
    
    if (!user) {
      return null;
    }

    const { data, error } = await supabaseClient
      .from('user_profiles')
      .select('id, email, full_name')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching current user info:', error);
      return {
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name
      };
    }

    return data;
  } catch (error) {
    console.error('Error fetching current user info:', error);
    return null;
  }
};

/**
 * Send email notification to admins for Local Government Administrator requests
 */
export const sendLocalGovAdminRequestEmail = async (
  request: LocalGovAdminRequestEmail
): Promise<{ success: boolean; error?: string }> => {
  try {
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '0.0.0.0' ||
                         window.location.port === '3000' ||
                         window.location.port === '3001' ||
                         window.location.port === '3002';

    if (isDevelopment) {
      console.log('📧 [LOCAL DEV] Local Gov Admin Request Email:');
      console.log('Requester:', request);
      console.log('📧 [LOCAL DEV] Email would be sent in production');
      return { success: true };
    }

    const { data, error } = await supabaseClient.functions.invoke('send-admin-request-email', {
      body: {
        request
      }
    });

    if (error) {
      console.error('Error calling send-admin-request-email function:', error);
      return {
        success: false,
        error: error.message
      };
    }

    console.log('✅ Admin request email sent successfully:', data);
    return { success: true };
  } catch (error) {
    console.error('Error sending admin request email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Send email to requester confirming Local Government Administrator request received
 */
export const sendLocalGovAdminRequestReceiptEmail = async (
  request: LocalGovAdminRequestReceiptEmail
): Promise<{ success: boolean; error?: string }> => {
  try {
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname === '0.0.0.0' ||
                         window.location.port === '3000' ||
                         window.location.port === '3001' ||
                         window.location.port === '3002';

    if (isDevelopment) {
      console.log('📧 [LOCAL DEV] Local Gov Admin Request Receipt Email:');
      console.log('Requester:', request);
      console.log('📧 [LOCAL DEV] Email would be sent in production');
      return { success: true };
    }

    const { data, error } = await supabaseClient.functions.invoke('send-user-request-email', {
      body: {
        request
      }
    });

    if (error) {
      console.error('Error calling send-user-request-email function:', error);
      return {
        success: false,
        error: error.message
      };
    }

    console.log('✅ User request receipt email sent successfully:', data);
    return { success: true };
  } catch (error) {
    console.error('Error sending user request receipt email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
